import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';
import { TestPlanService } from '../../../services/test-plan.service';
import { ReleaseService } from '../../../services/release.service';
import { TestCaseService } from '../../../services/test-case.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { AccountService } from '../../../account/account.service';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { TestPlan, TestPlanExecution } from '../../../models/test-plan.model';
import { TestRunService } from '../../../services/test-run.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { writeErrorKey } from '../test-plans.utils';
import { TestRun } from '../../../models/test-run.model';
import { Release } from '../../../models/release.model';
import { AutomationStatus, TestCase, TestCasePriority, TestCaseType, TestStep } from '../../../models/test-case.model';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { testRunExecutionsLink } from '../../../utils/test-run-routes';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';

interface TestPlanFormShape {
  title: FormControl<string>;
  releaseId: FormControl<string>;
  dueDate: FormControl<string>;
  assigneeUserId: FormControl<string>;
}

export type ExecutionKind = 'MANUAL' | 'AUTOMATED' | 'MIXED' | 'NONE';

@Component({
  selector: 'app-test-plan-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    DatePipe,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    RouterLink,
    PersonSelectComponent,
    Modal,
  ],
  templateUrl: './test-plan-form.component.html',
  styleUrl: './test-plan-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlanFormComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);
  private planService = inject(TestPlanService);
  private releaseService = inject(ReleaseService);
  private testCaseService = inject(TestCaseService);
  private projectMemberService = inject(ProjectMemberService);
  private snackBar = inject(MatSnackBar);
  private accountService = inject(AccountService);
  private translate = inject(TranslateService);
  private testRunService = inject(TestRunService);
  private projectRoleService = inject(ProjectRoleService);

  readonly projectId: string;
  planId: string | null;
  readonly isEdit: boolean;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorKey = signal('testPlans.form.saveError');
  readonly isReadOnly = computed(
    () => this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin(),
  );
  readonly plan = signal<TestPlan | null>(null);
  readonly testCases = signal<TestCase[]>([]);
  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly releases = signal<Release[]>([]);
  // Bei einem Ladefehler ist die Liste ebenfalls leer; der Hinweis gilt nur für eine erfolgreich geladene Liste.
  private readonly releasesLoaded = signal(false);
  readonly releasesEmpty = computed(() => this.releasesLoaded() && this.releases().length === 0);
  readonly selectedIds = signal<Set<string>>(new Set());
  readonly initialSelectedIds = signal<Set<string>>(new Set());

  readonly stepSelections = signal<Map<string, Set<number>>>(new Map());
  readonly expandedSteps = signal<Set<string>>(new Set());
  readonly stepsByCase = signal<Map<string, TestStep[]>>(new Map());
  readonly loadingSteps = signal<Set<string>>(new Set());

  readonly addMode = signal(true);

  readonly search = signal('');
  readonly typeFilter = signal<TestCaseType | ''>('');
  readonly priorityFilter = signal<TestCasePriority | ''>('');

  readonly fieldLengths = FIELD_LENGTHS;

  readonly execution = signal<TestPlanExecution | null>(null);
  readonly starting = signal(false);
  readonly executionError = signal(false);
  readonly executionErrorKey = signal('testPlans.form.sidebar.runError');
  private pollingSub: Subscription | null = null;

  readonly isRunning = computed(() => {
    const status = this.execution()?.status;
    return status === 'PENDING' || status === 'RUNNING';
  });
  readonly showRunning = computed(() => this.starting() || this.isRunning());
  readonly isDone = computed(() => this.execution()?.status === 'DONE');
  readonly isFailed = computed(() => this.execution()?.status === 'FAILED');

  /** DONE heißt nur „Testlauf angelegt“; Resultate gibt es erst, wenn der Lauf selbst abgeschlossen ist. */
  readonly createdRun = signal<TestRun | null>(null);
  private createdRunSub: Subscription | null = null;
  readonly createdRunId = computed(() => (this.isDone() ? (this.execution()?.testRunId ?? null) : null));
  readonly runCompleted = computed(() => !!this.createdRunId() && this.createdRun()?.status === 'COMPLETED');
  readonly showRunCreated = computed(() => !!this.createdRunId() && !this.runCompleted());
  readonly createdRunLink = computed(() => testRunExecutionsLink(this.projectId, this.createdRunId() ?? '', 'MANUAL'));

  readonly form: FormGroup<TestPlanFormShape> = this.fb.group({
    title: this.fb.control<string>('', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]),
    releaseId: this.fb.control<string>('', [Validators.required]),
    dueDate: this.fb.control<string>(''),
    assigneeUserId: this.fb.control<string>('', [Validators.required]),
  });

  readonly hasTestCases = computed(() => this.selectedIds().size > 0);

  /** Über die Gruppe statt das Control, weil der Edit-Modus ohne Control-Events vorbelegt. */
  private readonly releaseIdValue = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.controls.releaseId.value)),
    {
      initialValue: this.form.controls.releaseId.value,
    },
  );

  /**
   * Die Release-Liste kann (z. B. bei einem Ladefehler) leer sein; im Edit-Modus
   * trägt der Plan selbst noch das Label seines Releases.
   */
  readonly selectedReleaseLabel = computed<string | null>(() => {
    const id = this.releaseIdValue();
    if (!id) return null;
    const known = this.releases().find((r) => r.id === id)?.label;
    if (known) return known;
    const plan = this.plan();
    return plan?.releaseId === id ? plan.releaseLabel : id;
  });

  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));

  readonly availableTypes = computed(() => {
    const set = new Set<TestCaseType>();
    for (const tc of this.testCases()) {
      if (tc.type) set.add(tc.type);
    }
    return Array.from(set).sort();
  });

  readonly availablePriorities = computed(() => {
    const set = new Set<TestCasePriority>();
    for (const tc of this.testCases()) {
      if (tc.priority) set.add(tc.priority);
    }
    return Array.from(set).sort();
  });

  readonly filteredCandidates = computed(() => {
    const term = this.search().toLowerCase().trim();
    const typeF = this.typeFilter();
    const prioF = this.priorityFilter();
    return this.testCases().filter((tc) => {
      if (term) {
        const hay = `${tc.title} ${tc.key ?? ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (typeF && tc.type !== typeF) return false;
      if (prioF && tc.priority !== prioF) return false;
      return true;
    });
  });

  readonly selectedTestCases = computed(() => {
    const selected = this.selectedIds();
    return this.testCases().filter((tc) => selected.has(tc.id));
  });

  readonly visibleCandidates = computed(() => (this.addMode() ? this.filteredCandidates() : this.selectedTestCases()));

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly candidatesCount = computed(() => this.testCases().length);

  readonly selectionDirty = computed(() => {
    const current = this.selectedIds();
    const initial = this.initialSelectedIds();
    if (current.size !== initial.size) return true;
    for (const id of current) {
      if (!initial.has(id)) return true;
    }
    return false;
  });

  readonly executionKind = computed<ExecutionKind>(() => {
    const rows = this.selectedTestCases();
    if (rows.length === 0) return 'NONE';
    let manual = false;
    let automated = false;
    for (const tc of rows) {
      if (this.isAutomated(tc.automationStatus)) automated = true;
      else manual = true;
      if (manual && automated) return 'MIXED';
    }
    return automated ? 'AUTOMATED' : 'MANUAL';
  });

  readonly dirty = signal(false);
  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status,
  });
  readonly saveDisabled = computed(() => {
    if (this.saving() || this.formStatus() !== 'VALID') return true;
    if (!this.hasTestCases()) return true;
    if (this.isEdit && !this.dirty() && !this.selectionDirty()) return true;
    return false;
  });

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.planId = this.route.snapshot.paramMap.get('planId');
    this.isEdit = !!this.planId;
    this.addMode.set(!this.isEdit);

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.form.dirty) this.dirty.set(true);
    });

    if (this.isReadOnly()) {
      this.snackBar.open(this.translate.instant('testPlans.form.noWriteRight'), undefined, { duration: 4000 });
      this.router.navigate(['/projects', this.projectId, 'test-plans']);
      return;
    }
    this.loadInitialData();
  }

  private loadInitialData(): void {
    // NEX-829: Projekt-Mitglieder statt des TENANT_ADMIN-only Customers/Users-Endpunkts —
    // ein Nutzer mit Rolle USER darf diesen Endpunkt aufrufen und bekommt so
    // Verantwortlichen-Kandidaten (analog NEX-822).
    const users$ = this.projectMemberService.getMembers(this.projectId).pipe(
      catchError(() => {
        this.snackBar.open(this.translate.instant('testPlans.form.usersLoadError'), undefined, { duration: 4000 });
        return of([] as ProjectMemberDTO[]);
      }),
    );
    const releases$ = this.releaseService.list(this.projectId).pipe(
      tap(() => this.releasesLoaded.set(true)),
      catchError(() => {
        this.snackBar.open(this.translate.instant('testPlans.form.releasesLoadError'), undefined, { duration: 4000 });
        return of([] as Release[]);
      }),
    );
    const testCases$ = this.testCaseService.list(this.projectId).pipe(catchError(() => of([] as TestCase[])));
    const plan$ =
      this.isEdit && this.planId
        ? this.planService.getById(this.projectId, this.planId).pipe(
            map((p) => p as TestPlan | null),
            catchError(() => of(null)),
          )
        : of(null);
    // NEX-824: nur im Anlegen-Modus gebraucht (Self-Default); im Edit-Modus bleibt
    // der geladene Verantwortliche unverändert, daher kein zusätzlicher Request.
    const profile$ = this.isEdit ? of(null) : this.accountService.getProfile().pipe(catchError(() => of(null)));

    forkJoin({ users: users$, releases: releases$, testCases: testCases$, plan: plan$, profile: profile$ })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ users, releases, testCases, plan, profile }) => {
          this.users.set(users);
          this.releases.set(releases);
          this.testCases.set(testCases);
          if (plan) {
            this.plan.set(plan);
            this.form.patchValue(
              {
                title: plan.title,
                releaseId: plan.releaseId ?? '',
                dueDate: plan.dueDate ?? '',
                assigneeUserId: plan.assigneeUserId ?? '',
              },
              { emitEvent: false },
            );
            this.form.updateValueAndValidity();
            const set = new Set(plan.testCaseIds);
            this.selectedIds.set(set);
            this.initialSelectedIds.set(new Set(set));
            this.hydrateStepSelections(plan);
            this.loadExistingExecution(plan.id);
          } else if (this.isEdit) {
            this.loadError.set(true);
          } else if (profile) {
            // NEX-824: Self-Default — nur vorbelegen, wenn der aktuelle Nutzer unter
            // den geladenen Kandidaten ist; Pflichtfeld-Validierung ist hier bewusst
            // nicht Teil dieses Tickets (siehe NEX-819).
            prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, profile.id, this.personOptions());
          }
          this.form.markAsPristine();
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private hydrateStepSelections(plan: TestPlan): void {
    const map = new Map<string, Set<number>>();
    const raw = plan.stepSelections ?? {};
    for (const [caseId, idx] of Object.entries(raw)) {
      map.set(caseId, new Set(idx));
    }
    this.stepSelections.set(map);
  }

  private loadExistingExecution(planId: string): void {
    this.planService
      .getExecution(this.projectId, planId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((exec) => {
        if (exec) {
          this.applyExecution(exec);
          if (exec.status === 'PENDING' || exec.status === 'RUNNING') {
            this.startPolling(planId);
          }
        }
      });
  }

  toggle(tc: TestCase): void {
    this.selectedIds.update((set) => {
      const next = new Set(set);
      if (next.has(tc.id)) {
        next.delete(tc.id);
      } else {
        next.add(tc.id);
      }
      return next;
    });
    this.dirty.set(true);
  }

  isSelected(tc: TestCase): boolean {
    return this.selectedIds().has(tc.id);
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  onTypeChange(event: Event): void {
    this.typeFilter.set((event.target as HTMLSelectElement).value as TestCaseType | '');
  }

  onPriorityChange(event: Event): void {
    this.priorityFilter.set((event.target as HTMLSelectElement).value as TestCasePriority | '');
  }

  enterAddMode(): void {
    this.addMode.set(true);
  }

  exitAddMode(): void {
    this.addMode.set(false);
  }

  toggleStepsExpanded(tc: TestCase, event?: Event): void {
    event?.stopPropagation();
    this.expandedSteps.update((set) => {
      const next = new Set(set);
      if (next.has(tc.id)) next.delete(tc.id);
      else next.add(tc.id);
      return next;
    });
    if (this.expandedSteps().has(tc.id)) {
      this.ensureStepsLoaded(tc);
    }
  }

  private ensureStepsLoaded(tc: TestCase): void {
    if (this.stepsByCase().has(tc.id)) return;
    if (tc.steps && tc.steps.length) {
      this.applyLoadedSteps(tc.id, tc.steps);
      return;
    }
    this.loadingSteps.update((set) => {
      const next = new Set(set);
      next.add(tc.id);
      return next;
    });
    this.testCaseService
      .getById(this.projectId, tc.id)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((full) => {
        this.loadingSteps.update((set) => {
          const next = new Set(set);
          next.delete(tc.id);
          return next;
        });
        if (full && full.steps) this.applyLoadedSteps(tc.id, full.steps);
      });
  }

  private applyLoadedSteps(caseId: string, steps: TestStep[]): void {
    const ordered = [...steps].sort((a, b) => a.orderIndex - b.orderIndex);
    this.stepsByCase.update((map) => {
      const next = new Map(map);
      next.set(caseId, ordered);
      return next;
    });
    if (!this.stepSelections().has(caseId)) {
      this.stepSelections.update((map) => {
        const next = new Map(map);
        next.set(caseId, new Set(ordered.map((_, i) => i)));
        return next;
      });
    }
  }

  isStepExpanded(tc: TestCase): boolean {
    return this.expandedSteps().has(tc.id);
  }

  stepsFor(tc: TestCase): TestStep[] {
    return this.stepsByCase().get(tc.id) ?? [];
  }

  isStepLoading(tc: TestCase): boolean {
    return this.loadingSteps().has(tc.id);
  }

  isStepActive(tc: TestCase, stepIndex: number): boolean {
    const sel = this.stepSelections().get(tc.id);
    if (!sel) return true;
    return sel.has(stepIndex);
  }

  toggleStep(tc: TestCase, stepIndex: number, event?: Event): void {
    event?.stopPropagation();
    this.stepSelections.update((map) => {
      const next = new Map(map);
      const current = new Set(next.get(tc.id) ?? this.defaultStepSet(tc));
      if (current.has(stepIndex)) current.delete(stepIndex);
      else current.add(stepIndex);
      next.set(tc.id, current);
      return next;
    });
    this.dirty.set(true);
  }

  activeStepCount(tc: TestCase): number {
    const total = this.stepTotal(tc);
    const sel = this.stepSelections().get(tc.id);
    if (!sel) return total;
    let count = 0;
    for (let i = 0; i < total; i++) if (sel.has(i)) count++;
    return count;
  }

  stepTotal(tc: TestCase): number {
    const loaded = this.stepsByCase().get(tc.id);
    if (loaded) return loaded.length;
    return tc.stepCount ?? tc.steps?.length ?? 0;
  }

  isAutomated(status: AutomationStatus | undefined): boolean {
    return status === 'AUTOMATED' || status === 'AUTOMATABLE';
  }

  private defaultStepSet(tc: TestCase): Set<number> {
    const total = this.stepTotal(tc);
    const s = new Set<number>();
    for (let i = 0; i < total; i++) s.add(i);
    return s;
  }

  onCancel(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans']);
  }

  onSave(): void {
    if (this.saveDisabled()) return;
    const raw = this.form.getRawValue();
    const testCaseIds = Array.from(this.selectedIds());
    const stepSelections = this.buildStepSelectionsPayload(testCaseIds);
    this.saving.set(true);
    this.saveError.set(false);
    const request$ =
      this.isEdit && this.planId
        ? this.planService.update(this.projectId, this.planId, {
            title: raw.title.trim(),
            releaseId: raw.releaseId,
            dueDate: raw.dueDate || null,
            assigneeUserId: raw.assigneeUserId || null,
            clearAssignee: !raw.assigneeUserId,
            testCaseIds,
            stepSelections,
          })
        : this.planService.create(this.projectId, {
            title: raw.title.trim(),
            releaseId: raw.releaseId,
            dueDate: raw.dueDate || null,
            assigneeUserId: raw.assigneeUserId || null,
            testCaseIds,
            stepSelections,
          });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.dirty.set(false);
        this.form.markAsPristine();
        this.router.navigate(['/projects', this.projectId, 'test-plans']);
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.saveErrorKey.set(writeErrorKey(err, 'testPlans.form.saveError'));
        this.saveError.set(true);
      },
    });
  }

  private buildStepSelectionsPayload(caseIds: string[]): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    const selections = this.stepSelections();
    for (const id of caseIds) {
      const sel = selections.get(id);
      if (!sel) continue;
      out[id] = Array.from(sel).sort((a, b) => a - b);
    }
    return out;
  }

  onOpenReport(): void {
    const runId = this.createdRunId();
    if (runId) {
      if (this.runCompleted()) this.router.navigate(['/projects', this.projectId, 'test-results', runId]);
      return;
    }
    if (!this.planId) return;
    this.router.navigate(['/projects', this.projectId, 'test-plans', this.planId, 'report']);
  }

  readonly canRun = computed(
    () => this.isEdit && !this.showRunning() && !this.saving() && !this.dirty() && !this.selectionDirty(),
  );
  readonly canOpenResults = this.runCompleted;

  onRun(): void {
    if (!this.planId || !this.canRun()) return;
    this.executionError.set(false);
    this.starting.set(true);
    this.planService
      .execute(this.projectId, this.planId)
      .pipe(
        finalize(() => this.starting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (exec) => {
          this.applyExecution(exec);
          if (exec.status === 'PENDING' || exec.status === 'RUNNING') {
            this.startPolling(this.planId!);
          }
        },
        error: (err: unknown) => {
          this.executionErrorKey.set(writeErrorKey(err, 'testPlans.form.sidebar.runError'));
          this.executionError.set(true);
        },
      });
  }

  private startPolling(planId: string): void {
    this.stopPolling();
    this.pollingSub = timer(2000, 2000)
      .pipe(
        switchMap(() => this.planService.getExecution(this.projectId, planId).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((exec) => {
        if (!exec) return;
        this.applyExecution(exec);
        if (exec.status === 'DONE' || exec.status === 'FAILED') {
          this.stopPolling();
        }
      });
  }

  private applyExecution(exec: TestPlanExecution): void {
    this.execution.set(exec);
    this.createdRunSub?.unsubscribe();
    this.createdRunSub = null;
    this.createdRun.set(null);
    if (exec.status !== 'DONE' || !exec.testRunId) return;
    this.createdRunSub = this.testRunService
      .get(this.projectId, exec.testRunId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((run) => this.createdRun.set(run));
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = null;
  }

  hasUnsavedChanges(): boolean {
    return this.dirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges()) return of(true);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }
}
